import React, { useState, useEffect } from "react";
import "../../styles/common/fillUpsViewer.scss";
import { parseJsonRobustly } from "../../helper/jsonUtils";
import { AddIcon, EditIcon, DeleteIcon } from "../../icons";
import DeleteConfirmPopUp from "./deleteConfirmPopUp";
import GlobalLoader from "./GlobalLoader";
import {
  updatePageForCourse,
  updatePageForSubject,
  updatePageForTopic,
  updatePageForSubtopic,
  getPageForCourse,
  getPageForSubject,
  getPageForTopic,
  getPageForSubtopic,
} from "../../helper/apicalls/course";

const FillUpsViewer = ({
  questionData,
  onUpdate,
  pageId,
  pageData,
  context,
  courseKey,
  subjectKey,
  topicKey,
  subTopicKey,
  contentIndex,
  onAddNext,
  title = "Fill in the Blanks",
  permission,
  allowEditWithoutPersist = false,
  startInEditMode = false,
  externalSaveSignal,
  externalCancelSignal,
  provideUpdatedBuilder,
}) => {
  const [questions, setQuestions] = useState([]);
  const [editMode, setEditMode] = useState(!!startInEditMode);
  const [userAnswers, setUserAnswers] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const buildUpdatedItemFromOriginal = (originalItem) => {
    let out;
    if (typeof originalItem === "string") {
      try {
        out = JSON.parse(originalItem);
      } catch (e) {
        out = {};
      }
    } else {
      const cloned = JSON.parse(JSON.stringify(originalItem || {}));
      out = cloned && typeof cloned === "object" ? cloned : {};
    }
    if (out.content && typeof out.content === "string") {
      try {
        out.content = JSON.parse(out.content);
      } catch (e) {
        out.content = {};
      }
    }

    const content = {
      questions: questions.map((q) => ({
        question: q.question || "",
        answer: q.answer || "",
        ...(q.explanation !== undefined && { explanation: q.explanation }),
      })),
    };

    out.type = "fillups";
    out.content = JSON.stringify(content);
    return out;
  };

  useEffect(() => {
    if (typeof provideUpdatedBuilder === "function") {
      provideUpdatedBuilder((originalItem) =>
        buildUpdatedItemFromOriginal(originalItem),
      );
    }
  }, [provideUpdatedBuilder, questions]);

  const doUpdate = async () => {
    if (!pageId || !context) {
      if (allowEditWithoutPersist && typeof onUpdate === "function") {
        const tempOriginal = { type: "fillups", content: "{}" };
        const updatedItem = buildUpdatedItemFromOriginal(tempOriginal);
        onUpdate({ content: [updatedItem] });
        setEditMode(false);
      }
      return;
    }
    let contentArray = [];
    try {
      let rawContent = pageData?.content;
      if (typeof rawContent === "string") {
        const parsed = JSON.parse(rawContent);
        if (Array.isArray(parsed)) contentArray = parsed;
        else if (parsed && typeof parsed === "object") contentArray = [parsed];
      } else if (Array.isArray(rawContent)) {
        contentArray = rawContent;
      } else if (rawContent && typeof rawContent === "object") {
        contentArray = [rawContent];
      }
    } catch (e) {
      return;
    }

    if (!Array.isArray(contentArray)) return;
    if (
      typeof contentIndex !== "number" ||
      contentIndex < 0 ||
      contentIndex >= contentArray.length
    )
      return;

    const updatedArray = [...contentArray];
    const originalItem = updatedArray[contentIndex];
    const updatedItem = buildUpdatedItemFromOriginal(originalItem);
    updatedArray[contentIndex] = updatedItem;

    const payload = { id: pageId, content: updatedArray };
    let updated;

    setLoading(true);
    try {
      switch (context) {
        case "course":
          await updatePageForCourse(payload);
          updated = await getPageForCourse(courseKey, pageId);
          break;
        case "subject":
          await updatePageForSubject(payload);
          updated = await getPageForSubject(subjectKey, pageId);
          break;
        case "topic":
          await updatePageForTopic(payload);
          updated = await getPageForTopic(topicKey, pageId);
          break;
        case "subtopic":
          await updatePageForSubtopic(payload);
          updated = await getPageForSubtopic(subTopicKey, pageId);
          break;
        default:
          updated = payload;
      }
      if (onUpdate) onUpdate(updated);
      setEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    if (!pageId || !context) {
      if (allowEditWithoutPersist && typeof onUpdate === "function") {
        onUpdate({ content: [] });
        setIsDeleteConfirmOpen(false);
      }
      return;
    }
    if (!Array.isArray(pageData?.content)) return;
    if (
      typeof contentIndex !== "number" ||
      contentIndex < 0 ||
      contentIndex >= pageData.content.length
    )
      return;

    const updatedArray = pageData.content.filter(
      (_, idx) => idx !== contentIndex,
    );

    const payload = { id: pageId, content: updatedArray };
    let updated;

    setLoading(true);
    try {
      switch (context) {
        case "course":
          await updatePageForCourse(payload);
          updated = await getPageForCourse(courseKey, pageId);
          break;
        case "subject":
          await updatePageForSubject(payload);
          updated = await getPageForSubject(subjectKey, pageId);
          break;
        case "topic":
          await updatePageForTopic(payload);
          updated = await getPageForTopic(topicKey, pageId);
          break;
        case "subtopic":
          await updatePageForSubtopic(payload);
          updated = await getPageForSubtopic(subTopicKey, pageId);
          break;
        default:
          updated = payload;
      }

      if (onUpdate) onUpdate(updated);
      setIsDeleteConfirmOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const checkAnswer = (index) => {
    const question = questions[index];
    const userAnswer = (userAnswers[index] || "").trim().toLowerCase();
    const correctAnswer = (question.answer || "").trim().toLowerCase();
    return userAnswer === correctAnswer;
  };

  const Button = ({
    children,
    onClick,
    variant,
    size,
    className,
    disabled,
  }) => {
    const baseClass = "fillups-button";
    const variantClass = variant ? `fillups-button--${variant}` : "";
    const sizeClass = size ? `fillups-button--${size}` : "";
    const disabledClass = disabled ? "fillups-button--disabled" : "";
    const classes = `${baseClass} ${variantClass} ${sizeClass} ${disabledClass} ${
      className || ""
    }`;

    return (
      <button className={classes} onClick={onClick} disabled={disabled}>
        {children}
      </button>
    );
  };

  useEffect(() => {
    setQuestions([]);
    setUserAnswers({});
    setIsSubmitted(false);

    if (!questionData) return;

    try {
      let parsedData = questionData;

      if (typeof questionData === "string") {
        parsedData = parseJsonRobustly(questionData);
      }

      if (
        parsedData &&
        typeof parsedData === "object" &&
        parsedData.structured
      ) {
        parsedData = parsedData.structured;
      }

      if (Array.isArray(parsedData)) {
        setQuestions(parsedData);
      } else if (
        parsedData &&
        typeof parsedData === "object" &&
        parsedData.questions
      ) {
        setQuestions(parsedData.questions);
      } else if (parsedData && typeof parsedData === "object") {
        setQuestions([parsedData]);
      } else if (typeof questionData === "string") {
        setQuestions([
          {
            question: questionData,
            answer: "",
          },
        ]);
      }
    } catch (error) {
      console.error("Error parsing FillUps data:", error);
      setQuestions([
        {
          question:
            typeof questionData === "string"
              ? questionData
              : JSON.stringify(questionData, null, 2),
          answer: "",
        },
      ]);
    }
  }, [questionData]);

  useEffect(() => {
    setEditMode(!!startInEditMode);
  }, [startInEditMode]);

  useEffect(() => {
    if (!externalSaveSignal) return;
    if (typeof onUpdate !== "function") return;
    try {
      const tempOriginal = { type: "fillups", content: "{}" };
      const updatedItem = buildUpdatedItemFromOriginal(tempOriginal);
      onUpdate({ content: [updatedItem] });
    } catch (e) {
      console.error("Error updating FillUps data:", e);
    }
  }, [externalSaveSignal]);

  useEffect(() => {
    if (!externalCancelSignal) return;
    setQuestions((prev) => prev);
    setEditMode(!!startInEditMode);
  }, [externalCancelSignal]);

  return (
    <div
      className={`fillups-viewer fillups-viewer--${editMode ? "edit" : "view"}`}
    >
      {loading && <GlobalLoader visible={loading} text="Processing..." />}
      {isDeleteConfirmOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isDeleteConfirmOpen}
          setIsConfirmDeleteOpen={setIsDeleteConfirmOpen}
          handleDelete={doDelete}
          title="Delete fill in the blanks"
          message="Are you sure you want to delete this Fill in the Blanks item? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      )}
      <div className="fillups-viewer__header">
        <h3>{title}</h3>
        {questions.length > 0 &&
          (questions[0].categories || questions[0].category) && (
            <span className="question-category-chip">
              {String(questions[0].categories || questions[0].category)
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())}
            </span>
          )}
      </div>
      {questions.map((q, index) => {
        const question = q.question || q.title || q.text || "No question text";
        const answer = q.answer || "";
        const explanation = q.explanation || "";
        const isCorrect = isSubmitted ? checkAnswer(index) : null;

        return (
          <div
            key={index}
            className={`fillups-question fillups-question--${
              editMode ? "edit" : "view"
            }`}
          >
            {editMode ? (
              <div className="fillups-question__edit">
                <div
                  className={`fillups-textfield fillups-textfield--fullwidth fillups-question__input fillups-question__input--edit`}
                >
                  <label className="fillups-textfield__label">{`Question ${index + 1}`}</label>
                  <textarea
                    className="fillups-textfield__input"
                    rows={2}
                    value={q.question || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestions((prev) => {
                        const newQuestions = [...prev];
                        newQuestions[index] = {
                          ...newQuestions[index],
                          question: val,
                        };
                        return newQuestions;
                      });
                    }}
                  />
                </div>
                <div
                  className={`fillups-textfield fillups-textfield--fullwidth fillups-question__input`}
                >
                  <label className="fillups-textfield__label">
                    Correct Answer
                  </label>
                  <input
                    className="fillups-textfield__input"
                    value={answer}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestions((prev) => {
                        const newQuestions = [...prev];
                        newQuestions[index] = {
                          ...newQuestions[index],
                          answer: val,
                        };
                        return newQuestions;
                      });
                    }}
                  />
                </div>
                <div
                  className={`fillups-textfield fillups-textfield--fullwidth fillups-question__explanation fillups-question__explanation--edit`}
                >
                  <label className="fillups-textfield__label">
                    Explanation
                  </label>
                  <textarea
                    className="fillups-textfield__input"
                    rows={2}
                    value={explanation}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestions((prev) => {
                        const newQuestions = [...prev];
                        newQuestions[index] = {
                          ...newQuestions[index],
                          explanation: val,
                        };
                        return newQuestions;
                      });
                    }}
                  />
                </div>
              </div>
            ) : (
              <>
                <div
                  className={`fillups-textfield fillups-textfield--fullwidth fillups-question__input fillups-question__input--view fillups-textfield--readonly`}
                >
                  <label className="fillups-textfield__label">{`Question ${index + 1}`}</label>
                  <textarea
                    className="fillups-textfield__input"
                    rows={2}
                    value={q.question || ""}
                    readOnly={true}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestions((prev) => {
                        const newQuestions = [...prev];
                        newQuestions[index] = {
                          ...newQuestions[index],
                          question: val,
                        };
                        return newQuestions;
                      });
                    }}
                  />
                </div>
                <div
                  className={`fillups-textfield fillups-textfield--fullwidth fillups-question__input fillups-question__answer--view fillups-textfield--readonly`}
                >
                  <label className="fillups-textfield__label">
                    Correct Answer
                  </label>
                  <input
                    className="fillups-textfield__input"
                    value={answer}
                    readOnly={true}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestions((prev) => {
                        const newQuestions = [...prev];
                        newQuestions[index] = {
                          ...newQuestions[index],
                          answer: val,
                        };
                        return newQuestions;
                      });
                    }}
                  />
                </div>
                {explanation && !editMode && (
                  <div className="fillups-explanation">
                    <strong>Explanation:</strong> {explanation}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      {
        <div className="fillups-viewer__actions">
          {permission &&
            (pageId || allowEditWithoutPersist) &&
            (editMode ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setEditMode(false)}
                  className="fillups-viewer__action-button"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={doUpdate}
                  className="fillups-viewer__action-button"
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setEditMode(true)}
                  className="fillups-viewer__action-button"
                >
                  <EditIcon className="toolbar-button-icon" />
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIsDeleteConfirmOpen(true)}
                  className="fillups-viewer__action-button"
                >
                  <DeleteIcon className="toolbar-button-icon" />
                  Delete
                </Button>
                {onAddNext && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onAddNext}
                    className="fillups-viewer__action-button"
                  >
                    <AddIcon className="toolbar-button-icon" />
                    Add Next
                  </Button>
                )}
              </>
            ))}
        </div>
      }
    </div>
  );
};

export default FillUpsViewer;
